import { Office } from 'api/profile/types';

export interface PreferredOfficeEntry {
  FirmLocationId: number;
  FirmLocationJurisdictionID: number;
  BusinessName: string;
  StateId: number;
  StateCode: string;
  UnderwriterId: number;
  UserId: number;
  FirmID: number;
  ALTAID: string;
  FirmName: string;
  UnderwriterName: string;
  IsSelected: number;
}

export interface PreferredOfficeResult {
  filteredOffices: Office[];
  isFiltering: boolean;
  showPreferredOfficeChip: boolean;
}

/**
 * Filters firm offices based on user's preferred office settings.
 * Returns the filtered offices and whether filtering is actively narrowing results.
 */
export function getFilteredOfficesByPreference(
  preferredOffice: PreferredOfficeEntry[] | undefined,
  offices: Office[] | undefined,
  firmId: string | undefined,
): PreferredOfficeResult {
  if (!offices || !firmId || !preferredOffice || preferredOffice.length === 0) {
    return { filteredOffices: offices ?? [], isFiltering: false, showPreferredOfficeChip: false };
  }

  const firmEntries = preferredOffice.filter((entry) => String(entry.FirmID) === String(firmId));
  if (firmEntries.length === 0) {
    return { filteredOffices: offices, isFiltering: false, showPreferredOfficeChip: false };
  }

  const isPreferredOfficeSelected = firmEntries.filter((entry) => entry.IsSelected === 1);
  const selectedEntries = isPreferredOfficeSelected.length > 0 ? isPreferredOfficeSelected : firmEntries;

  const allowedAccountIds = new Set(
    selectedEntries.map((entry: PreferredOfficeEntry) => entry.FirmLocationJurisdictionID),
  );

  const filteredOffices = offices
    .map((office) => ({
      ...office,
      accounts: office.accounts.filter(
        (account) => account.accountId !== undefined && allowedAccountIds.has(account.accountId),
      ),
    }))
    .filter((office) => office.accounts.length > 0);

  const isFiltering = filteredOffices.length < offices.length;

  const showPreferredOfficeChip =
    isPreferredOfficeSelected.length > 0 && isPreferredOfficeSelected.length !== firmEntries.length;

  return { filteredOffices, isFiltering, showPreferredOfficeChip };
}
